import React from 'react';

export interface ModernPageHeaderProps {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    left?: React.ReactNode;
    actions?: React.ReactNode;
    className?: string;
    children?: React.ReactNode;
    ariaLabel?: string;
}

const ModernPageHeader: React.FC<ModernPageHeaderProps> = ({ title, subtitle, icon, left, actions, className = '', children, ariaLabel }) => {

    return (
        <section
            aria-label={ariaLabel ?? title}
            className={[
                'relative flex-shrink-0 mb-4',
                'rounded-2xl border border-[var(--v2-border-card)]',
                'bg-[var(--v2-bg-card)] shadow-card',
                className,
            ].join(' ')}
        >
            <div aria-hidden className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                <div
                    className="absolute inset-0"
                    style={{ background: 'var(--v2-bg-hero)' }}
                />

                <div
                    className="absolute inset-x-0 top-0 h-[3px]"
                    style={{
                        background:
                            'linear-gradient(90deg, var(--v2-primary-500) 0%, var(--v2-primary-400) 55%, transparent 100%)',
                    }}
                />
            </div>

            <div className="relative z-10 flex items-center gap-3 px-5 h-[52px]">

                {icon && (
                    <span
                        className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg"
                        style={{
                            background: 'var(--v2-primary-50, rgba(4,94,184,0.08))',
                            color: 'var(--v2-primary-500)',
                        }}
                    >
                        {icon}
                    </span>
                )}

                <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
                    <h1 className="text-[15px] font-bold tracking-tight text-[var(--v2-text-heading)] whitespace-nowrap m-0 leading-none">
                        {title}
                    </h1>

                    {subtitle && (
                        <>
                            <span
                                className="hidden sm:block w-px h-3.5 flex-shrink-0"
                                style={{ background: 'var(--v2-border-subtle)' }}
                                aria-hidden
                            />
                            <p className="hidden sm:block text-[12px] text-[var(--v2-text-muted)] m-0">
                                {subtitle}
                            </p>
                        </>
                    )}
                </div>

                {left ? (
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        {left}
                    </div>
                ) : (
                    <div className="flex-1" aria-hidden />
                )}

                {actions && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {actions}
                    </div>
                )}
            </div>

            {children && (
                <div className="relative z-10 border-t border-[var(--v2-border-subtle)] px-5 py-3">
                    {children}
                </div>
            )}
        </section>
    );
};

export default ModernPageHeader;
